import React from 'react';
import {
  View,
  Text,
  FlatList,
  Pressable,
  StyleSheet,
  SafeAreaView,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import Ionicons from '@expo/vector-icons/Ionicons';
import Feather from '@expo/vector-icons/Feather';
import {
  Paprika,
  PaprikaTypography,
  PaprikaZones,
  PaprikaShadows,
} from '../../../styles/theme';
import { PantrySnapshot } from '../../../redux/pantrySelectors';
import { PantryLocation } from '../../../utils/pantryLocation';
import { ZONE_META } from '../zoneMeta';
import { PantryIcon } from '../PantryIcon';
import { PantryItemRow } from '../PantryItemRow';

interface Props {
  zone: PantryLocation;
  snapshot: PantrySnapshot;
  onBack: () => void;
  onItemPress: (id: string) => void;
  onPhoto: () => void;
  onQuickAdd: () => void;
}

export function ZoneView({
  zone,
  snapshot,
  onBack,
  onItemPress,
  onPhoto,
  onQuickAdd,
}: Props) {
  const { t } = useTranslation();
  const z = PaprikaZones[zone];
  const meta = ZONE_META[zone];
  const items = snapshot.byLocation[zone];

  return (
    <SafeAreaView style={[styles.root, { backgroundColor: Paprika.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <Pressable
          onPress={onBack}
          style={styles.backChip}
          accessibilityLabel={t('navigation.back')}
        >
          <Feather name="chevron-left" size={20} color={Paprika.textPrimary} />
        </Pressable>
        <View style={[styles.zoneIconChip, { backgroundColor: z.cap }]}>
          <PantryIcon icon={meta.icon} size={19} color={z.accent} />
        </View>
        <View style={styles.headerText}>
          <Text style={styles.zoneTitle}>{t(meta.labelKey)}</Text>
          <Text style={[styles.headerTitle, { color: z.ink }]}>
            {t(meta.condKey)} · {t('pantry.itemsCount', { count: items.length })}
          </Text>
        </View>
      </View>

      {/* List */}
      {items.length > 0 ? (
        <FlatList
          data={items}
          keyExtractor={(item) => item.item.id}
          renderItem={({ item }) => (
            <PantryItemRow
              view={item}
              onPress={() => onItemPress(item.item.id)}
            />
          )}
          scrollEnabled={true}
          contentContainerStyle={styles.listContent}
        />
      ) : (
        <View style={styles.empty}>
          <Ionicons
            name="checkmark-circle"
            size={48}
            color={Paprika.textMuted}
          />
          <Text style={styles.emptyText}>
            {t('pantry.zoneEmpty')}
          </Text>
        </View>
      )}

      {/* Footer Buttons */}
      <View style={styles.footer}>
        <Pressable
          onPress={onPhoto}
          style={({ pressed }) => [
            styles.button,
            styles.buttonSolid,
            {
              backgroundColor: z.accent,
              transform: [{ scale: pressed ? 0.97 : 1 }],
            },
          ]}
        >
          <Ionicons
            name="camera"
            size={18}
            color={Paprika.onPrimary}
            style={styles.buttonIcon}
          />
          <Text style={[styles.buttonText, { color: Paprika.onPrimary }]}>
            {t('pantry.add.photograph')}
          </Text>
        </Pressable>

        <Pressable
          onPress={onQuickAdd}
          style={({ pressed }) => [
            styles.button,
            styles.buttonDashed,
            {
              borderColor: z.accent,
              transform: [{ scale: pressed ? 0.97 : 1 }],
            },
          ]}
        >
          <Feather
            name="plus"
            size={18}
            color={z.accent}
            style={styles.buttonIcon}
          />
          <Text style={[styles.buttonText, { color: z.accent }]}>
            {t('pantry.add.addManual')}
          </Text>
        </Pressable>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 20,
    backgroundColor: Paprika.surfaceLight,
  },
  zoneIconChip: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  zoneTitle: {
    ...PaprikaTypography.headingMedium,
    fontSize: 20,
    lineHeight: 22,
    color: Paprika.textPrimary,
  },
  headerTitle: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    lineHeight: 14,
    marginTop: 3,
  },
  listContent: {
    paddingHorizontal: 0,
    paddingBottom: 16,
  },
  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  emptyText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    color: Paprika.textMuted,
    textAlign: 'center',
  },
  footer: {
    flexDirection: 'row',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 13,
    borderTopWidth: 1,
    borderTopColor: Paprika.hairline,
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 13,
    borderRadius: 13,
  },
  buttonSolid: {
    // Solid button styling
  },
  buttonDashed: {
    borderWidth: 1.5,
    borderStyle: 'dashed',
  },
  buttonIcon: {
    // Icon styling handled by size prop
  },
  buttonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    lineHeight: 18,
  },
});
